import { useState } from "react";
import { updateWATemplate } from "../services/watemplateService";

const staticFields = [
  { model: 'Subscription', app: 'subscription', field: 'name', label: 'subscription name' },
  { model: 'Personal', app: 'personal', field: 'first_name', label: 'person name' },
];

const WATemplateDetail = ({ template, onBack }) => {
  const [formData, setFormData] = useState({
    id: template.id,
    name: template.name || "",
    channel: template.channel || "whatsapp",
    body: template.body || "",
    variables: template.variables || [],
    template_id: template.template_id || "",
    template_name: template.template_name || "",
    campaign_id: template.campaign_id || "",
    active: template.active || "true",
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteVariable = (index) => {
    const updatedVariables = formData.variables.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, variables: updatedVariables }));
  };

  const handleVariableChange = (index, field, value) => {
    const updatedVariables = [...formData.variables];
    updatedVariables[index][field] = value;
    setFormData((prev) => ({ ...prev, variables: updatedVariables }));
  };

  const handleAddVariable = () => {
    setFormData((prev) => ({
      ...prev,
      variables: [...prev.variables, { model: "", app: "", field: "" }],
    }));
  };

  const handleSave = async () => {
    setSuccessMsg("");
    setErrorMsg("");

    if (!formData.name.trim()) {
      setErrorMsg("Name is required.");
      return;
    }

    if (!formData.channel) {
      setErrorMsg("Channel is required.");
      return;
    }

    if (formData.channel === "whatsapp" && !formData.template_name.trim()) {
      setErrorMsg("Template Name is required for WhatsApp.");
      return;
    }

    const allVariablesValid = formData.variables.every(
      (v) => v.model.trim() && v.app.trim() && v.field.trim()
    );

    if (!allVariablesValid) {
      setErrorMsg("All variable rows must have Model, App, and Field filled for WhatsApp.");
      return;
    }

    if (formData.channel === "sms") {
      if (!formData.template_id.trim()) {
        setErrorMsg("Template ID is required for SMS.");
        return;
      }
      if (!formData.campaign_id.trim()) {
        setErrorMsg("Campaign ID is required for SMS.");
        return;
      }
    }

    setLoading(true);

    const payload = {
      name: formData.name,
      channel: formData.channel,
      body: formData.body,
      variables: formData.variables,
      active: formData.active
    };

    if (formData.channel === "sms") {
      payload.template_id = formData.template_id;
      payload.campaign_id = formData.campaign_id;
    } else if (formData.channel === "whatsapp") {
      payload.template_name = formData.template_name;
    }

    try {
      await updateWATemplate(formData.id, payload);
      setSuccessMsg("Template updated successfully.");
    } catch (error) {
      setErrorMsg("Failed to update template.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <button
        onClick={onBack}
        className="mb-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        ← Back
      </button>

      <h2 className="text-2xl font-semibold mb-4">Edit WA Template</h2>

      {successMsg && <p className="text-green-600 mb-4">{successMsg}</p>}
      {errorMsg && <p className="text-red-600 mb-4">{errorMsg}</p>}

      <div className="space-y-4">
        <InputField label="Name" name="name" value={formData.name} onChange={handleChange} />

        <div>
          <label className="block font-medium">Channel:</label>
          <select
            name="channel"
            value={formData.channel}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        <div>
          <label className="block font-medium">Active:</label>
          <select
            name="active"
            value={formData.active}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>

        {formData.channel === "sms" && (
          <>
            <InputField label="Template ID" name="template_id" value={formData.template_id} onChange={handleChange} />
            <InputField label="Campaign ID" name="campaign_id" value={formData.campaign_id} onChange={handleChange} />
          </>
        )}

        {formData.channel === "whatsapp" && (
          <InputField label="Template Name" name="template_name" value={formData.template_name} onChange={handleChange} />
        )}

        <div>
          <label className="block font-medium">Body:</label>
          <textarea
            name="body"
            value={formData.body}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
            rows={2}
          />
        </div>

        <div className="flex gap-4 items-center justify-between">
          <label className="block font-medium">Variables:</label>
          <button
            onClick={handleAddVariable}
            className="mt-2 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
          >
            + Add Variable
          </button>
        </div>

        {formData.variables.map((variable, index) => {
          const matchedField = staticFields.find(
            (f) =>
              f.model === variable.model &&
              f.app === variable.app &&
              f.field === variable.field
          );

          return (
            <div key={index} className="grid grid-cols-1 gap-2 mt-2 items-center">
              <div className="flex gap-2">
                <select
                  value={matchedField?.label || ""}
                  onChange={(e) => {
                    const selectedField = staticFields.find(f => f.label === e.target.value);
                    if (selectedField) {
                      handleVariableChange(index, "model", selectedField.model);
                      handleVariableChange(index, "app", selectedField.app);
                      handleVariableChange(index, "field", selectedField.field);
                    }
                  }}
                  className="border rounded px-3 py-2 w-full"
                >
                  <option value="">Select Field</option>
                  {staticFields.map((f) => (
                    <option key={f.label} value={f.label}>
                      {f.label}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => handleDeleteVariable(index)}
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                  title="Delete Variable"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="mt-6 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
      >
        {loading ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

const InputField = ({ label, name, value, onChange }) => (
  <div>
    <label className="block font-medium">{label}:</label>
    <input
      name={name}
      value={value}
      onChange={onChange}
      className="border rounded px-3 py-2 w-full"
    />
  </div>
);

export default WATemplateDetail;
